import { Link } from 'react-router-dom';
import { Wifi, Waves, Trees, Car, ShieldCheck, Tv, Percent, Clock, Check, ArrowRight, Users } from 'lucide-react';
import { Seo } from '@/components/Seo';
import { Container } from '@/components/ui/Container';
import { SectionHeading } from '@/components/ui/SectionHeading';
import { Photo } from '@/components/ui/Photo';
import { ROOMS, AMENITIES, STAY_POLICY } from '@/data/accommodation';
import { formatPula } from '@/lib/money';
import { ROOM_IMAGE_KEY } from '@/data/images';
import { useImages } from '@/lib/images';
import { useReveal } from '@/lib/useReveal';

const FACILITY_ICONS = [Waves, Trees, Percent, Car, ShieldCheck];

export function Stay() {
  useReveal();
  const img = useImages();
  return (
    <>
      <Seo title="Stay with us" description="Grace Haven BnB — self-catering rooms in Gaborone with a pool, garden, fast WiFi, and a discount on spa services." />

      {/* Hero */}
      <section className="relative flex min-h-[60vh] items-center overflow-hidden bg-cream pt-24">
        <Photo src={img.suite} alt="A calm bedroom at Grace Haven BnB" className="absolute inset-0" imgClassName="opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-r from-cream via-cream/85 to-transparent" />
        <Container className="relative z-10">
          <p className="eyebrow mb-4">Grace Haven BnB</p>
          <h1 className="max-w-xl font-display text-5xl font-medium leading-tight text-espresso sm:text-6xl">Stay a little longer.</h1>
          <div className="rule-gold mt-6" />
          <p className="mt-6 max-w-lg font-body font-light leading-relaxed text-muted">
            Self-catering comfort beside the spa — make a weekend of it.
          </p>
          <Link to="/stay/book" className="btn-primary mt-8">Book a room <ArrowRight size={16} /></Link>
        </Container>
      </section>

      {/* Rooms showcase */}
      <section className="bg-cream py-24">
        <Container>
          <SectionHeading eyebrow="Accommodation" title="Our rooms" center intro="Comfortable, calm, and close to the spa. Rates per night in Botswana Pula." />
          <div className="mt-16 space-y-16">
            {ROOMS.map((r, i) => (
              <article key={r.id} className="reveal grid items-center gap-8 lg:grid-cols-2">
                <Photo src={img[ROOM_IMAGE_KEY[r.id]] || r.image || img.room} alt={r.name} className={`aspect-[4/3] w-full rounded-sm shadow-soft ${i % 2 ? 'lg:order-2' : ''}`} />
                <div className={i % 2 ? 'lg:order-1' : ''}>
                  <div className="flex items-center gap-3">
                    <h3 className="font-display text-3xl text-espresso">{r.name}</h3>
                    <span className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1 font-body text-[11px] uppercase tracking-wide text-muted"><Users size={12} /> {r.sleeps}</span>
                  </div>
                  <div className="rule-gold mt-4" />
                  <p className="mt-5 font-body leading-relaxed text-muted">{r.blurb}</p>
                  {r.features && (
                    <ul className="mt-5 flex flex-wrap gap-2">
                      {r.features.map((f) => (
                        <li key={f} className="rounded-full border border-line bg-paper px-3 py-1.5 font-body text-xs text-espresso">{f}</li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-7 flex flex-wrap items-center gap-5">
                    <span className="font-display text-3xl text-espresso">{formatPula(r.price)} <span className="font-body text-sm text-muted">/ night</span></span>
                    <Link to={`/stay/book?room=${r.id}`} className="btn-primary">Book this room <ArrowRight size={15} /></Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </Container>
      </section>

      {/* Amenities */}
      <section className="bg-paper py-24">
        <Container>
          <div className="grid gap-14 lg:grid-cols-2">
            <div className="reveal">
              <p className="eyebrow mb-3 flex items-center gap-2"><Tv size={16} /> Self-catering</p>
              <h2 className="font-display text-3xl text-espresso">Settle right in</h2>
              <div className="rule-gold mt-4" />
              <ul className="mt-6 flex flex-wrap gap-3">
                {AMENITIES.selfCatering.map((a) => (
                  <li key={a} className="rounded-full border border-line bg-cream px-4 py-2 font-body text-sm text-espresso">{a}</li>
                ))}
                <li className="flex items-center gap-1.5 rounded-full border border-line bg-cream px-4 py-2 font-body text-sm text-espresso"><Wifi size={14} /> High-speed WiFi</li>
              </ul>
            </div>
            <div className="reveal">
              <p className="eyebrow mb-3">Facilities</p>
              <h2 className="font-display text-3xl text-espresso">More to enjoy</h2>
              <div className="rule-gold mt-4" />
              <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                {AMENITIES.facilities.map((f, i) => {
                  const Icon = FACILITY_ICONS[i % FACILITY_ICONS.length];
                  return (
                    <li key={f} className="flex items-center gap-3 font-body text-sm text-muted">
                      <Icon size={18} className="shrink-0 text-gold-deep" /> {f}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Good to know */}
          <div className="reveal mt-20 border-t border-line pt-14">
            <SectionHeading eyebrow="Good to know" title="Check-in & house notes" center intro="A few details to help you plan. Anything else, just ask." />
            <div className="mx-auto mt-10 grid max-w-4xl gap-6 lg:grid-cols-[260px_1fr]">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
                <div className="border border-line bg-cream p-5 text-center lg:text-left">
                  <Clock size={18} className="mx-auto text-gold-deep lg:mx-0" />
                  <p className="mt-2 font-body text-[11px] uppercase tracking-wide text-muted">Check-in</p>
                  <p className="font-display text-xl text-espresso">{STAY_POLICY.checkIn}</p>
                </div>
                <div className="border border-line bg-cream p-5 text-center lg:text-left">
                  <Clock size={18} className="mx-auto text-gold-deep lg:mx-0" />
                  <p className="mt-2 font-body text-[11px] uppercase tracking-wide text-muted">Check-out</p>
                  <p className="font-display text-xl text-espresso">{STAY_POLICY.checkOut}</p>
                </div>
              </div>
              <ul className="grid gap-3 sm:grid-cols-2">
                {STAY_POLICY.conditions.map((c) => (
                  <li key={c} className="flex items-start gap-2.5 font-body text-sm leading-relaxed text-muted">
                    <Check size={16} className="mt-0.5 shrink-0 text-gold-deep" /> {c}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-12 text-center">
              <Link to="/stay/book" className="btn-primary">Check availability &amp; book <ArrowRight size={16} /></Link>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
